
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import {
  ISbStoryData,
  StoryblokClient,
  StoryblokComponent,
  getStoryblokApi,
} from '@storyblok/react'
import { GetServerSidePropsContextWithLocale, NextPage } from 'next'
import { NextSeo } from 'next-seo'
import { useEffect } from 'react'
import Layout from '@/app/common/components/Layout'
import { GraphQLOperationError } from '@/app/common/graphql/error'
import {
  commonRelations,
  getWithRelationsAndLinks,
} from '@/app/common/storyblok'
import config from '@/config'
import { createGetServerSideProps } from '@/helpers/createGetServerSideProps'
import { isPreview } from '@/helpers/setCacheControl'
import * as tracking from '@/helpers/tracking'
import { SelectedProduct } from '@/types'
import {
  GetProductBySlugDocument,
  GetProductBySlugQuery,
  GetProductBySlugQueryVariables,
} from '@/types/gql/graphql'
import {
  NewProductCategoryPageStoryblok,
  NewProductPageStoryblok,
} from '@/types/storyblok-component-types'
import { isNotFoundError } from '@/utils/error'
import { dayjs } from '@/utils/time'

interface ProductPageProps {
  initialQueryVariables: GetProductBySlugQueryVariables
  selectedProduct: SelectedProduct
  story: ISbStoryData<NewProductPageStoryblok | NewProductCategoryPageStoryblok>
}

const ProductPage: NextPage<ProductPageProps> = ({
  initialQueryVariables: getProductbySlugQueryVariables,
  selectedProduct,
  story,
}) => {
  useEffect(() => {
    tracking.viewItem([selectedProduct])
  }, [selectedProduct])

  return (
    <Layout>
      <NextSeo
        title={selectedProduct?.name ? selectedProduct.name : undefined}
        nofollow={true}
        noindex={true}
      />

      {story.content.body?.map((block) => (
        <StoryblokComponent
          key={block._uid}
          blok={block}
          selectedProduct={selectedProduct}
          initialQueryVariables={getProductbySlugQueryVariables}
        />
      ))}
    </Layout>
  )
}

const PRODUCTS_CDN_MAX_AGE = 3600

 const _getServerSideProps = createGetServerSideProps({
  async getServerSideProps({ context, client }) {
    context.res.setHeader(
      'Cache-Control',
      isPreview(context)
        ? 'no-cache, no-store, must-revalidate'
        : `public, max-age=120, s-maxage=${PRODUCTS_CDN_MAX_AGE}, stale-while-revalidate=1800, public`,
    )
    const now = dayjs().toDate()

    const initialQueryVariables: GetProductBySlugQueryVariables = {
      slug: context.query.slug as string,
      date: now.toISOString(),
    }

    let product: GetProductBySlugQuery['getProductBySlug'] | undefined
    try {
      product = (
        await client.executeQuery<
          GetProductBySlugQuery,
          GetProductBySlugQueryVariables
        >({
          document: GetProductBySlugDocument,
          variables: initialQueryVariables,
          staleTime: PRODUCTS_CDN_MAX_AGE,
        })
      )?.data?.getProductBySlug
    } catch (error) {
      if (isNotFoundError(error as GraphQLOperationError)) {
        return { notFound: true }
      }

      throw error
    }

    if (!product) {
      throw new Error('No item page content found')
    }

    const storyblokApi = getStoryblokApi()

    const [productPage, additionalProductPage] = await fetchNewProductPages(
      storyblokApi,
      product._id,
      context,
    )

    if (additionalProductPage) {
      console.warn(
        'Found more than one product page for product id',
        product._id,
      )
    }

    if (productPage) {
      return {
        props: {
          initialQueryVariables,
          selectedProduct: product,
          story: productPage,
        },
      }
    }

    const categoryIds: string[] = product?.category?.parentCategories.map(
      (category) => category._id,
    )

    const productCategoryPages = await fetchNewProductCategoryPages(
      storyblokApi,
      categoryIds,
      context,
    )

    for (const id of [...categoryIds].reverse()) {
      const [categoryPage, additionalCategoryPage] =
        productCategoryPages.filter((pc) => pc.content.category === id)

      if (additionalCategoryPage) {
        console.warn(
          'Found more than one product category page for category id',
          id,
        )
      }

      if (categoryPage) {
        return {
          props: {
            initialQueryVariables,
            selectedProduct: product,
            story: categoryPage,
          },
        }
      }
    }

    return { notFound: true }
  },
})

async function fetchNewProductPages(
  storyblokApi: StoryblokClient,
  productId: string,
  context: GetServerSidePropsContextWithLocale,
): Promise<ISbStoryData<NewProductPageStoryblok>[]> {
  const { data } = await getWithRelationsAndLinks({
    slug: 'cdn/stories',
    storyblokApi,
    context,
    params: {
      resolve_relations: commonRelations,
      filter_query: {
        product: {
          in: productId,
        },
      },
      content_type: 'new-product-page',
    },
  })

  if (!isPreview(context) && !config.showPlaygroundStories) {
    return (data.stories as ISbStoryData<NewProductPageStoryblok>[]).filter(
      (story) => !story.full_slug.includes('/playground/'),
    )
  }

  return data.stories
}

async function fetchNewProductCategoryPages(
  storyblokApi: StoryblokClient,
  categoryIds: string[],
  context: GetServerSidePropsContextWithLocale,
): Promise<ISbStoryData<NewProductCategoryPageStoryblok>[]> {
  const { data } = await getWithRelationsAndLinks({
    slug: 'cdn/stories',
    storyblokApi,
    context,
    params: {
      resolve_relations: commonRelations,
      filter_query: {
        category: {
          in: categoryIds.join(','),
        },
      },
      content_type: 'new-product-category-page',
    },
  })
  return data.stories
}

export default ProductPage


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/products/[slug]',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  